import { Shell } from '@/components/Shell';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { Pill, stateLabel, stateToTone } from '@/components/ui/Pill';
import { adminApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/customers/$id')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: CustomerDetailPage,
});

function CustomerDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'customer', id],
    queryFn: () => adminApi.customerDetail(id),
  });
  const [reason, setReason] = useState('');

  const block = useMutation({
    mutationFn: () => adminApi.blockCustomer(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customer', id] });
      setReason('');
    },
  });
  const unblock = useMutation({
    mutationFn: () => adminApi.unblockCustomer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'customer', id] }),
  });

  if (isLoading || !data) {
    return (
      <Shell page="…" sub="CLIENTE · A CARREGAR">
        <div style={{ padding: 24, color: 'var(--lv-gray-500)' }}>A carregar…</div>
      </Shell>
    );
  }

  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short' });

  const initials = (data.name ?? data.email)
    .split(' ')
    .map((p: string) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Shell page={data.name ?? data.email} sub={`CLIENTE · ${data.email.toUpperCase()}`}>
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, alignItems: 'start' }}>
        {/* Profile card */}
        <div style={{ display: 'grid', gap: 16 }}>
          <section style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
            {/* Avatar + name */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  background: 'var(--lv-brand-deep)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{data.name ?? '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--lv-gray-500)', marginTop: 2 }}>{data.email}</div>
              </div>
            </div>

            <div className="label" style={{ color: 'var(--lv-brand)', marginBottom: 12 }}>
              PERFIL
            </div>
            <div className="h-rule" style={{ marginBottom: 16 }} />

            <dl style={{ display: 'grid', gap: '8px 0' }}>
              {[
                ['Telefone', data.phone ?? '—'],
                ['País', data.country ?? '—'],
                ['Idioma', data.localePref.toUpperCase()],
                ['Moeda', data.currencyPref],
                ['Marketing', data.marketingOptIn ? 'SIM' : 'NÃO'],
                ['Pontos', String(data.pointsBalance)],
                ['Registo', dateFmt.format(new Date(data.createdAt))],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, fontSize: 13 }}
                >
                  <dt style={dtStyle}>{label}</dt>
                  <dd style={{ margin: 0, fontWeight: 600 }}>{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Block / unblock */}
          <section style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
            <div
              className="label"
              style={{ color: data.blockedAt ? 'var(--lv-err)' : 'var(--lv-gray-500)', marginBottom: 12 }}
            >
              {data.blockedAt ? 'BLOQUEADO' : 'ESTADO DA CONTA'}
            </div>
            {data.blockedAt ? (
              <>
                <p style={{ fontSize: 12, color: 'var(--lv-err)', marginTop: 0 }}>
                  Bloqueado em {dateFmt.format(new Date(data.blockedAt))}.
                </p>
                {data.blockedReason && (
                  <p style={{ fontSize: 12, color: 'var(--lv-gray-500)', marginTop: 4 }}>
                    {data.blockedReason}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => unblock.mutate()}
                  disabled={unblock.isPending}
                  style={btnPrimary}
                >
                  {unblock.isPending ? '…' : 'Desbloquear'}
                </button>
              </>
            ) : (
              <>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Motivo do bloqueio (obrigatório)"
                  rows={3}
                  style={{
                    width: '100%',
                    marginTop: 0,
                    padding: 10,
                    border: '1px solid var(--lv-line)',
                    fontSize: 13,
                    borderRadius: 0,
                    resize: 'vertical',
                  }}
                />
                <div style={{ marginTop: 8 }}>
                  <ConfirmButton
                    label={block.isPending ? '…' : 'Bloquear cliente'}
                    question="Bloquear este cliente? Deixa de poder reservar."
                    onConfirm={() => block.mutate()}
                    disabled={block.isPending || reason.trim().length < 3}
                    style={btnDanger}
                  />
                </div>
              </>
            )}
          </section>
        </div>

        {/* Activity panels */}
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Bookings */}
          <ActivityCard title="RESERVAS" count={data.bookings.length}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {data.bookings.length === 0 ? (
                  <tr>
                    <td style={{ padding: '12px 0', color: 'var(--lv-gray-500)' }}>Sem reservas.</td>
                  </tr>
                ) : (
                  data.bookings.map((b) => (
                    <tr key={b.id} style={{ borderBottom: '1px solid var(--lv-line)' }}>
                      <td style={{ padding: '10px 0' }}>
                        <Link
                          to="/bookings/$id"
                          params={{ id: b.id }}
                          style={{ fontWeight: 700, color: 'var(--lv-brand)', textDecoration: 'none' }}
                        >
                          {b.code}
                        </Link>
                      </td>
                      <td style={{ padding: '10px 8px', color: 'var(--lv-gray-500)' }}>
                        {dateFmt.format(new Date(b.fromAt))} → {dateFmt.format(new Date(b.toAt))}
                      </td>
                      <td style={{ padding: '10px 0', textAlign: 'right' }}>
                        <Pill label={stateLabel(b.state)} tone={stateToTone(b.state)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ActivityCard>

          {/* Payments */}
          <ActivityCard title="PAGAMENTOS" count={data.payments.length}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {data.payments.length === 0 ? (
                  <tr>
                    <td style={{ padding: '12px 0', color: 'var(--lv-gray-500)' }}>Sem pagamentos.</td>
                  </tr>
                ) : (
                  data.payments.map((p) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--lv-line)' }}>
                      <td style={{ padding: '10px 0', color: 'var(--lv-gray-500)' }}>
                        {dateFmt.format(new Date(p.createdAt))}
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{ fontWeight: 600 }}>{p.kind}</span>{' '}
                        <span style={{ color: 'var(--lv-gray-500)' }}>· {p.method}</span>
                      </td>
                      <td
                        style={{
                          padding: '10px 8px',
                          textAlign: 'right',
                          fontWeight: 700,
                          fontFamily: 'var(--lv-font-display)',
                          fontSize: 15,
                        }}
                      >
                        {p.amountEur.toFixed(2)} €
                      </td>
                      <td style={{ padding: '10px 0', textAlign: 'right' }}>
                        <Pill label={stateLabel(p.state)} tone={stateToTone(p.state)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ActivityCard>

          {/* Reviews */}
          <ActivityCard title="AVALIAÇÕES" count={data.reviews.length}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {data.reviews.length === 0 ? (
                  <tr>
                    <td style={{ padding: '12px 0', color: 'var(--lv-gray-500)' }}>Sem avaliações.</td>
                  </tr>
                ) : (
                  data.reviews.map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--lv-line)' }}>
                      <td style={{ padding: '10px 0', color: 'var(--lv-amber)', fontSize: 14 }}>
                        {'★'.repeat(r.stars)}
                      </td>
                      <td style={{ padding: '10px 8px', color: 'var(--lv-gray-500)' }}>
                        {dateFmt.format(new Date(r.createdAt))}
                      </td>
                      <td style={{ padding: '10px 0', textAlign: 'right' }}>
                        <Pill label={stateLabel(r.state)} tone={stateToTone(r.state)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ActivityCard>
        </div>
      </div>
    </Shell>
  );
}

function ActivityCard({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}
      >
        <div className="label" style={{ color: 'var(--lv-brand)' }}>
          {title}
        </div>
        <span
          className="display"
          style={{ fontSize: 20, color: count > 0 ? 'var(--lv-ink)' : 'var(--lv-gray-500)' }}
        >
          {count}
        </span>
      </div>
      <div className="h-rule" style={{ marginBottom: 14 }} />
      {children}
    </div>
  );
}

const dtStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--lv-gray-500)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  paddingTop: 2,
};
const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '10px 18px',
  marginTop: 12,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  display: 'inline-block',
};
const btnDanger: React.CSSProperties = {
  background: '#fff',
  color: 'var(--lv-err)',
  border: '1px solid var(--lv-err)',
  padding: '10px 18px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  display: 'inline-block',
};
